/**
 * Offboarding — docs/plans/offboarding/offboarding-module-plan.md.
 *
 * Mirrors onboarding.contract.ts's Template -> Stage -> TaskTemplate shape, instantiated per
 * person as Process -> Task -> Note (see that file for the pattern this one intentionally copies).
 * Two sets of routes below (resignations, fnf-settlements) are MOVED from employee.contract.ts,
 * not new — bodies are unchanged, only the path prefix changes, per the plan's §5 decision that
 * exit is a lifecycle, not a profile field, and old paths are removed rather than aliased
 * (CLAUDE.md: change the code, don't add shims).
 *
 * Configurability (plan §3) is the point of the OffboardingPolicy / ClearanceFunction / stage-flag
 * design below: nothing about the pipeline shape is a fixed enum except OffboardingTaskKind, which
 * is a rendering hint (which widget the frontend shows), not a business rule.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  emailSchema,
  errorBodySchema,
  exitTypeSchema,
  idempotencyKeySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  EXIT_INTERVIEW_QUESTION_TYPES,
  OFFBOARDING_ASSIGNEE_ROLES,
  OFFBOARDING_PROCESS_STATUSES,
  OFFBOARDING_TASK_KINDS,
  OFFBOARDING_TASK_STATUSES,
} from '../enums.js';
import { routeMeta } from './meta.js';

const c = initContract();

const assigneeRoleSchema = z.enum(OFFBOARDING_ASSIGNEE_ROLES);
const taskKindSchema = z.enum(OFFBOARDING_TASK_KINDS);
const taskStatusSchema = z.enum(OFFBOARDING_TASK_STATUSES);
const processStatusSchema = z.enum(OFFBOARDING_PROCESS_STATUSES);
const questionTypeSchema = z.enum(EXIT_INTERVIEW_QUESTION_TYPES);

// ---------------------------------------------------------------------------
// Resignation Request Schemas — MOVED from employee.contract.ts, unchanged bodies.
// ---------------------------------------------------------------------------

export const offboardingResignationRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  employeeName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  reasonCategory: z.string(),
  reasonDetails: z.string(),
  requestedLastWorkingDay: z.string(),
  approvedLastWorkingDay: z.string().nullable().optional(),
  noticePeriodDays: z.number(),
  status: z.enum(['PENDING', 'APPROVED', 'REJECTED', 'REVOKE_REQUESTED', 'REVOKED']),
  revokeReason: z.string().nullable().optional(),
  rejectionNote: z.string().nullable().optional(),
  reviewerNote: z.string().nullable().optional(),
  submittedAt: z.string(),
  reviewedAt: z.string().nullable().optional(),
  reviewedByPersonId: uuidSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OffboardingResignationRequestDto = z.infer<typeof offboardingResignationRequestSchema>;

export const offboardingSubmitResignationBodySchema = z.object({
  reasonCategory: z.enum([
    'Better Opportunity',
    'Personal/Family',
    'Career Change',
    'Higher Education',
    'Relocation',
    'Other',
  ]),
  reasonDetails: z.string().trim().min(5, 'Please provide more detail for your resignation').max(2000),
  requestedLastWorkingDay: dateOnlySchema,
  noticePeriodDays: z.number().int().min(0).max(365).default(30),
});

export const offboardingListResignationRequestsQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
  status: z.enum(['all', 'PENDING', 'APPROVED', 'REJECTED', 'REVOKE_REQUESTED', 'REVOKED']).optional(),
});

export const offboardingApproveResignationBodySchema = z.object({
  approvedLastWorkingDay: dateOnlySchema.optional(),
  reviewerNote: z.string().trim().max(1000).optional(),
});
export const offboardingRejectResignationBodySchema = z.object({
  rejectionNote: z.string().trim().min(1, 'Rejection note is required').max(1000),
});
export const offboardingRevokeResignationBodySchema = z.object({
  revokeReason: z.string().trim().min(5, 'Reason for revocation is required').max(1000),
});
export const offboardingApproveRevokeBodySchema = z.object({
  reviewerNote: z.string().trim().max(1000).optional(),
});
export const offboardingRejectRevokeBodySchema = z.object({
  rejectionNote: z.string().trim().min(1, 'Rejection note is required').max(1000),
});

// ---------------------------------------------------------------------------
// Full & Final Settlement Schemas — MOVED from employee.contract.ts, unchanged bodies.
// ---------------------------------------------------------------------------

const nonNegativeDecimalStringSchema = decimalStringSchema.refine(
  (value) => Number(value) >= 0,
  'Must be zero or greater',
);

export const offboardingOffboardingFnfDeductionLineSchema = z.object({
  label: z.string().trim().min(1).max(200),
  amount: nonNegativeDecimalStringSchema,
});
export type OffboardingFnfDeductionLine = z.infer<typeof offboardingOffboardingFnfDeductionLineSchema>;

export const offboardingFnfSettlementSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  employeeName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  resignationRequestId: uuidSchema.nullable().optional(),
  processId: uuidSchema.nullable().optional(),
  currency: z.string(),
  pendingSalaryAmount: z.string().nullable().optional(),
  leaveEncashmentDays: z.string().nullable().optional(),
  leaveEncashmentAmount: z.string().nullable().optional(),
  gratuityAmount: z.string().nullable().optional(),
  bonusAmount: z.string().nullable().optional(),
  deductions: z.array(offboardingOffboardingFnfDeductionLineSchema).nullable().optional(),
  netPayableAmount: z.string(),
  status: z.enum(['pending_approval', 'approved', 'rejected', 'paid']),
  preparedByPersonId: uuidSchema,
  reviewerNote: z.string().nullable().optional(),
  settledOn: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OffboardingFnfSettlementDto = z.infer<typeof offboardingFnfSettlementSchema>;

export const offboardingCreateFnfSettlementBodySchema = z
  .object({
    employmentId: uuidSchema,
    resignationRequestId: uuidSchema.optional(),
    processId: uuidSchema.optional(),
    currency: currencySchema,
    pendingSalaryAmount: nonNegativeDecimalStringSchema.optional(),
    leaveEncashmentDays: nonNegativeDecimalStringSchema.optional(),
    leaveEncashmentAmount: nonNegativeDecimalStringSchema.optional(),
    gratuityAmount: nonNegativeDecimalStringSchema.optional(),
    bonusAmount: nonNegativeDecimalStringSchema.optional(),
    deductions: z.array(offboardingOffboardingFnfDeductionLineSchema).max(20).optional(),
  })
  .refine(
    (value) =>
      value.pendingSalaryAmount !== undefined ||
      value.leaveEncashmentAmount !== undefined ||
      value.gratuityAmount !== undefined ||
      value.bonusAmount !== undefined ||
      (value.deductions?.length ?? 0) > 0,
    { message: 'Provide at least one settlement component' },
  );

export const offboardingListFnfSettlementsQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
  status: z.enum(['all', 'pending_approval', 'approved', 'rejected', 'paid']).optional(),
});
export const offboardingDecideFnfSettlementBodySchema = z.object({
  comment: z.string().trim().max(1000).optional(),
});
export const offboardingMarkFnfSettlementPaidBodySchema = z.object({
  settledOn: dateOnlySchema,
});

// ---------------------------------------------------------------------------
// Policy & clearance functions — plan §3's Settings screen.
// ---------------------------------------------------------------------------

export const offboardingPolicySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  legalEntityId: uuidSchema.nullable(),
  defaultNoticePeriodDays: z.number().int().min(0).max(365),
  resignationRequiresApproval: z.boolean(),
  exitInterviewRequired: z.boolean(),
  autoArchiveDaysAfterLwd: z.number().int().min(0).max(90),
  reminderLeadDays: z.array(z.number().int().min(0).max(180)),
  blockArchiveOnUnreturnedAssets: z.boolean(),
  /** Open a return request on the leaver's register assets when the exit starts (Assets module). */
  autoRequestAssetReturns: z.boolean(),
  updatedAt: instantSchema,
});
export type OffboardingPolicyDto = z.infer<typeof offboardingPolicySchema>;

export const getOffboardingPolicyQuerySchema = z.object({ legalEntityId: uuidSchema.optional() });
export const updateOffboardingPolicyBodySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  defaultNoticePeriodDays: z.number().int().min(0).max(365).optional(),
  resignationRequiresApproval: z.boolean().optional(),
  exitInterviewRequired: z.boolean().optional(),
  autoArchiveDaysAfterLwd: z.number().int().min(0).max(90).optional(),
  reminderLeadDays: z.array(z.number().int().min(0).max(180)).max(10).optional(),
  blockArchiveOnUnreturnedAssets: z.boolean().optional(),
  autoRequestAssetReturns: z.boolean().optional(),
});

export const clearanceFunctionSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  orderIndex: z.number().int(),
  isActive: z.boolean(),
});
export type ClearanceFunctionDto = z.infer<typeof clearanceFunctionSchema>;

export const createClearanceFunctionBodySchema = z.object({
  name: z.string().trim().min(1).max(100),
});
export const updateClearanceFunctionBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  orderIndex: z.number().int().optional(),
  isActive: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Templates — Template -> Stage -> TaskTemplate, the configurable pipeline shape (plan §3).
// ---------------------------------------------------------------------------

export const offboardingTaskTemplateSchema = z.object({
  id: uuidSchema,
  stageId: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  assigneeRole: assigneeRoleSchema,
  taskKind: taskKindSchema,
  clearanceFunctionId: uuidSchema.nullable(),
  clearanceFunctionName: z.string().nullable(),
  isRequired: z.boolean(),
  isVisibleToEmployee: z.boolean(),
  requiresUpload: z.boolean(),
  dueDaysBeforeLwd: z.number().int(),
  orderIndex: z.number().int(),
});
export type OffboardingTaskTemplateDto = z.infer<typeof offboardingTaskTemplateSchema>;

export const offboardingStageSchema = z.object({
  id: uuidSchema,
  templateId: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  orderIndex: z.number().int(),
  countsAsNoticePeriod: z.boolean(),
  isArchiveStage: z.boolean(),
  requireAllTasksToAdvance: z.boolean(),
  isVisibleToEmployee: z.boolean(),
  taskTemplates: z.array(offboardingTaskTemplateSchema),
});
export type OffboardingStageDto = z.infer<typeof offboardingStageSchema>;

export const offboardingTemplateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  legalEntityId: uuidSchema.nullable(),
  locationId: uuidSchema.nullable(),
  departmentId: uuidSchema.nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  stages: z.array(offboardingStageSchema),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type OffboardingTemplateDto = z.infer<typeof offboardingTemplateSchema>;

export const createOffboardingTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  legalEntityId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  isDefault: z.boolean().default(false),
});
export const updateOffboardingTemplateBodySchema = createOffboardingTemplateBodySchema.partial().extend({
  isActive: z.boolean().optional(),
});

export const createStageBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  countsAsNoticePeriod: z.boolean().default(false),
  isArchiveStage: z.boolean().default(false),
  requireAllTasksToAdvance: z.boolean().default(false),
  isVisibleToEmployee: z.boolean().default(true),
});
export const updateStageBodySchema = createStageBodySchema.partial();
export const reorderStagesBodySchema = z.object({
  stageIds: z.array(uuidSchema).min(1).max(30),
});

export const createTaskTemplateBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  assigneeRole: assigneeRoleSchema,
  taskKind: taskKindSchema.default('generic'),
  clearanceFunctionId: uuidSchema.optional(),
  isRequired: z.boolean().default(true),
  isVisibleToEmployee: z.boolean().default(true),
  requiresUpload: z.boolean().default(false),
  dueDaysBeforeLwd: z.number().int().min(0).max(180).default(0),
});
export const updateTaskTemplateBodySchema = createTaskTemplateBodySchema.partial();

// ---------------------------------------------------------------------------
// Processes & tasks — the per-person instantiation.
// ---------------------------------------------------------------------------

export const offboardingTaskSchema = z.object({
  id: uuidSchema,
  processId: uuidSchema,
  stageId: uuidSchema,
  stageName: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  assigneeRole: assigneeRoleSchema,
  assigneePersonId: uuidSchema.nullable(),
  assigneeName: z.string().nullable(),
  taskKind: taskKindSchema,
  clearanceFunctionId: uuidSchema.nullable(),
  clearanceFunctionName: z.string().nullable(),
  status: taskStatusSchema,
  dueDate: dateOnlySchema,
  isRequired: z.boolean(),
  requiresUpload: z.boolean(),
  attachmentUrl: z.string().url().nullable(),
  handoverToPersonId: uuidSchema.nullable(),
  handoverToName: z.string().nullable(),
  completedAt: instantSchema.nullable(),
  completedByName: z.string().nullable(),
  notes: z.string().nullable(),
});
export type OffboardingTaskDto = z.infer<typeof offboardingTaskSchema>;

export const offboardingProcessSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  employmentId: uuidSchema,
  personId: uuidSchema,
  employeeCode: z.string().nullable(),
  employeeName: z.string(),
  /** Null for a phone-only employee (no work email). */
  email: emailSchema.nullable(),
  departmentName: z.string().nullable(),
  designation: z.string().nullable(),
  managerName: z.string().nullable(),
  templateId: uuidSchema,
  templateName: z.string(),
  resignationRequestId: uuidSchema.nullable(),
  exitType: exitTypeSchema,
  noticeStartsOn: dateOnlySchema,
  lastWorkingDay: dateOnlySchema,
  daysRemaining: z.number().int().nullable(),
  currentStageId: uuidSchema.nullable(),
  currentStageName: z.string().nullable(),
  status: processStatusSchema,
  taskCompletedCount: z.number().int().nonnegative(),
  taskTotalCount: z.number().int().nonnegative(),
  archivedAt: instantSchema.nullable(),
  cancelledReason: z.string().nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type OffboardingProcessDto = z.infer<typeof offboardingProcessSchema>;

/** Full detail — the process plus its tasks grouped implicitly by stageId (client groups them). */
export const offboardingProcessDetailSchema = offboardingProcessSchema.extend({
  tasks: z.array(offboardingTaskSchema),
});
export type OffboardingProcessDetailDto = z.infer<typeof offboardingProcessDetailSchema>;

export const listProcessesQuerySchema = paginationQuerySchema.extend({
  stageId: uuidSchema.optional(),
  status: processStatusSchema.optional(),
  departmentId: uuidSchema.optional(),
  q: z.string().trim().max(200).optional(),
});

export const startProcessBodySchema = z.object({
  employmentId: uuidSchema,
  templateId: uuidSchema.optional(),
  exitType: exitTypeSchema,
  noticeStartsOn: dateOnlySchema,
  lastWorkingDay: dateOnlySchema,
});

export const moveStageBodySchema = z.object({ stageId: uuidSchema });
export const cancelProcessBodySchema = z.object({
  cancelledReason: z.string().trim().min(1).max(1000),
});

export const updateOffboardingTaskBodySchema = z.object({
  status: taskStatusSchema.optional(),
  assigneePersonId: uuidSchema.nullable().optional(),
  notes: z.string().max(2_000).nullable().optional(),
  attachmentUrl: z.string().url().nullable().optional(),
});
export const assignHandoverBodySchema = z.object({ toPersonId: uuidSchema });
export const createOffboardingTaskBodySchema = z.object({
  stageId: uuidSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2_000).optional(),
  assigneeRole: assigneeRoleSchema.default('EMPLOYEE'),
  taskKind: taskKindSchema.default('generic'),
  clearanceFunctionId: uuidSchema.optional(),
  dueDate: dateOnlySchema,
  isRequired: z.boolean().default(true),
  requiresUpload: z.boolean().default(false),
});
export const listMyTasksQuerySchema = paginationQuerySchema.extend({
  status: taskStatusSchema.optional(),
});

// ---------------------------------------------------------------------------
// Dashboard & notice tracker
// ---------------------------------------------------------------------------

export const stageCountSchema = z.object({
  stageId: uuidSchema,
  stageName: z.string(),
  orderIndex: z.number().int(),
  count: z.number().int().nonnegative(),
});

export const offboardingDashboardSchema = z.object({
  activeOffboardingCount: z.number().int().nonnegative(),
  exitRatePercent: z.number().nullable(),
  resignationsThisCycle: z.number().int().nonnegative(),
  resignationsApproved: z.number().int().nonnegative(),
  resignationsPending: z.number().int().nonnegative(),
  taskCompletionPercent: z.number().min(0).max(100),
  tasksCompleted: z.number().int().nonnegative(),
  tasksTotal: z.number().int().nonnegative(),
  archivedCount: z.number().int().nonnegative(),
  noticesEndingThisWeek: z.number().int().nonnegative(),
  stageCounts: z.array(stageCountSchema),
  resignationStatusBreakdown: z.object({
    requested: z.number().int().nonnegative(),
    approved: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
  }),
  unreturnedAssetCount: z.number().int().nonnegative(),
});
export type OffboardingDashboardDto = z.infer<typeof offboardingDashboardSchema>;

export const getDashboardQuerySchema = z.object({
  departmentId: uuidSchema.optional(),
  legalEntityId: uuidSchema.optional(),
});

export const noticeTrackerEntrySchema = z.object({
  processId: uuidSchema,
  personId: uuidSchema,
  employeeName: z.string(),
  avatarUrl: z.string().nullable(),
  currentStageName: z.string().nullable(),
  lastWorkingDay: dateOnlySchema,
  daysRemaining: z.number().int().nullable(),
  ended: z.boolean(),
});
export type NoticeTrackerEntry = z.infer<typeof noticeTrackerEntrySchema>;

// ---------------------------------------------------------------------------
// Exit interview
// ---------------------------------------------------------------------------

export const exitInterviewQuestionSchema = z.object({
  id: z.string().min(1).max(100),
  prompt: z.string().min(1).max(500),
  type: questionTypeSchema,
  options: z.array(z.string().max(200)).max(20).optional(),
  required: z.boolean().default(false),
});
export type ExitInterviewQuestion = z.infer<typeof exitInterviewQuestionSchema>;

export const exitInterviewTemplateSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  isActive: z.boolean(),
  questions: z.array(exitInterviewQuestionSchema),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type ExitInterviewTemplateDto = z.infer<typeof exitInterviewTemplateSchema>;

export const createInterviewTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  questions: z.array(exitInterviewQuestionSchema).min(1).max(50),
});
export const updateInterviewTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  isActive: z.boolean().optional(),
  questions: z.array(exitInterviewQuestionSchema).min(1).max(50).optional(),
});

export const exitInterviewAnswerSchema = z.object({
  questionId: z.string().min(1).max(100),
  value: z.union([z.string().max(2000), z.number(), z.array(z.string())]),
});

export const exitInterviewResponseSchema = z.object({
  id: uuidSchema,
  processId: uuidSchema,
  templateId: uuidSchema,
  answers: z.array(exitInterviewAnswerSchema),
  conductedByPersonId: uuidSchema.nullable(),
  conductedByName: z.string().nullable(),
  conductedAt: instantSchema.nullable(),
  wouldRecommend: z.boolean().nullable(),
  primaryReason: z.string().nullable(),
  createdAt: instantSchema,
});
export type ExitInterviewResponseDto = z.infer<typeof exitInterviewResponseSchema>;

export const submitExitInterviewBodySchema = z.object({
  answers: z.array(exitInterviewAnswerSchema).min(1).max(100),
});
export const recordExitInterviewBodySchema = submitExitInterviewBodySchema;

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

export const employeeAssetSchema = z.object({
  id: uuidSchema,
  employmentId: uuidSchema,
  processId: uuidSchema.nullable(),
  employeeName: z.string(),
  assetTag: z.string().nullable(),
  assetType: z.string(),
  description: z.string().nullable(),
  issuedOn: dateOnlySchema,
  returnedOn: dateOnlySchema.nullable(),
  returnCondition: z.string().nullable(),
  returnedToName: z.string().nullable(),
});
export type EmployeeAssetDto = z.infer<typeof employeeAssetSchema>;

export const addEmployeeAssetBodySchema = z.object({
  assetTag: z.string().trim().max(100).optional(),
  assetType: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).optional(),
  issuedOn: dateOnlySchema,
});
export const markAssetReturnedBodySchema = z.object({
  returnedOn: dateOnlySchema,
  returnCondition: z.string().trim().max(200).optional(),
});
export const listUnreturnedAssetsQuerySchema = paginationQuerySchema;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const offboardingContract = c.router(
  {
    // ---- Resignations (moved) --------------------------------------------------
    submitResignation: {
      method: 'POST',
      path: '/resignations',
      body: offboardingSubmitResignationBodySchema,
      responses: { 201: offboardingResignationRequestSchema, 401: errorBodySchema, 400: errorBodySchema },
      summary: 'Submit a resignation request (Employee Self-Service)',
      metadata: routeMeta(null),
    },
    getMyResignationRequests: {
      method: 'GET',
      path: '/resignations/my',
      responses: { 200: z.array(offboardingResignationRequestSchema), 401: errorBodySchema },
      summary: 'Get caller’s resignation request status and history',
      metadata: routeMeta(null),
    },
    listResignationRequests: {
      method: 'GET',
      path: '/resignations',
      query: offboardingListResignationRequestsQuerySchema,
      responses: { 200: paginatedSchema(offboardingResignationRequestSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'List all resignation requests for HR review',
      metadata: routeMeta('offboarding:read'),
    },
    approveResignation: {
      method: 'POST',
      path: '/resignations/:requestId/approve',
      body: offboardingApproveResignationBodySchema,
      responses: { 200: offboardingResignationRequestSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Approve a resignation request, starting its workflow and the offboarding process',
      // Not offboarding:manage — same shape as decideFnfSettlement: the assigned workflow
      // approver may decide without holding any permission at all (a role/hr_admin step
      // routinely names someone who may hold neither), so the handler's own OR'd check
      // (isAssignee || can(offboarding:read)) has no unconditional guard to declare here.
      metadata: routeMeta(null),
    },
    rejectResignation: {
      method: 'POST',
      path: '/resignations/:requestId/reject',
      body: offboardingRejectResignationBodySchema,
      responses: { 200: offboardingResignationRequestSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Reject a resignation request',
      metadata: routeMeta(null),
    },
    revokeResignation: {
      method: 'POST',
      path: '/resignations/:requestId/revoke',
      body: offboardingRevokeResignationBodySchema,
      responses: { 200: offboardingResignationRequestSchema, 401: errorBodySchema, 400: errorBodySchema, 404: errorBodySchema },
      summary: 'Employee requests revocation of submitted/approved resignation',
      metadata: routeMeta(null),
    },
    approveRevokeResignation: {
      method: 'POST',
      path: '/resignations/:requestId/approve-revoke',
      body: offboardingApproveRevokeBodySchema,
      responses: { 200: offboardingResignationRequestSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'HR approves revocation request and restores employment active status',
      metadata: routeMeta('offboarding:manage'),
    },
    rejectRevokeResignation: {
      method: 'POST',
      path: '/resignations/:requestId/reject-revoke',
      body: offboardingRejectRevokeBodySchema,
      responses: { 200: offboardingResignationRequestSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'HR rejects revocation request',
      metadata: routeMeta('offboarding:manage'),
    },

    // ---- Full & Final Settlements (moved) --------------------------------------
    createFnfSettlement: {
      method: 'POST',
      path: '/fnf-settlements',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: offboardingCreateFnfSettlementBodySchema,
      responses: {
        201: offboardingFnfSettlementSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Record a Full & Final settlement for an exiting employee and start its approval',
      metadata: routeMeta('fnf:execute', { salaryPin: 'others' }),
    },
    getMyFnfSettlements: {
      method: 'GET',
      path: '/fnf-settlements/my',
      responses: { 200: z.array(offboardingFnfSettlementSchema), 401: errorBodySchema },
      summary: 'Get caller’s own Full & Final settlement records',
      metadata: routeMeta(null),
    },
    listFnfSettlements: {
      method: 'GET',
      path: '/fnf-settlements',
      query: offboardingListFnfSettlementsQuerySchema,
      responses: { 200: paginatedSchema(offboardingFnfSettlementSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'List Full & Final settlements for HR/Finance review',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },
    approveFnfSettlement: {
      method: 'POST',
      path: '/fnf-settlements/:settlementId/approve',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: offboardingDecideFnfSettlementBodySchema,
      responses: {
        200: offboardingFnfSettlementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve a Full & Final settlement through its @hrms/workflow approval step',
      metadata: routeMeta(null, { salaryPin: 'others' }),
    },
    rejectFnfSettlement: {
      method: 'POST',
      path: '/fnf-settlements/:settlementId/reject',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: offboardingDecideFnfSettlementBodySchema,
      responses: {
        200: offboardingFnfSettlementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject a Full & Final settlement through its @hrms/workflow approval step',
      metadata: routeMeta(null, { salaryPin: 'others' }),
    },
    markFnfSettlementPaid: {
      method: 'POST',
      path: '/fnf-settlements/:settlementId/mark-paid',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: offboardingMarkFnfSettlementPaidBodySchema,
      responses: {
        200: offboardingFnfSettlementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Record that an approved settlement has actually been paid out',
      metadata: routeMeta('fnf:execute', { salaryPin: 'others' }),
    },

    // ---- Policy & clearance functions (plan §3 Settings screen) ---------------
    getPolicy: {
      method: 'GET',
      path: '/policy',
      query: getOffboardingPolicyQuerySchema,
      responses: { 200: offboardingPolicySchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Get the resolved offboarding policy (legal-entity override, else org default)',
      metadata: routeMeta('offboarding.policy:manage'),
    },
    updatePolicy: {
      method: 'PUT',
      path: '/policy',
      body: updateOffboardingPolicyBodySchema,
      responses: { 200: offboardingPolicySchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Update the org-default or a legal-entity offboarding policy',
      metadata: routeMeta('offboarding.policy:manage'),
    },
    listClearanceFunctions: {
      method: 'GET',
      path: '/clearance-functions',
      responses: { 200: z.array(clearanceFunctionSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'List org-defined clearance functions',
      metadata: routeMeta('offboarding.policy:manage'),
    },
    createClearanceFunction: {
      method: 'POST',
      path: '/clearance-functions',
      body: createClearanceFunctionBodySchema,
      responses: { 201: clearanceFunctionSchema, 401: errorBodySchema, 403: errorBodySchema, 409: errorBodySchema },
      summary: 'Add a clearance function (e.g. a new clearing department)',
      metadata: routeMeta('offboarding.policy:manage'),
    },
    updateClearanceFunction: {
      method: 'PATCH',
      path: '/clearance-functions/:functionId',
      body: updateClearanceFunctionBodySchema,
      responses: {
        200: clearanceFunctionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Rename/reorder/activate/deactivate a clearance function',
      metadata: routeMeta('offboarding.policy:manage'),
    },
    deleteClearanceFunction: {
      method: 'DELETE',
      path: '/clearance-functions/:functionId',
      body: c.noBody(),
      responses: {
        204: c.noBody(),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete a clearance function — refused (409) if any active task references it',
      metadata: routeMeta('offboarding.policy:manage'),
    },

    // ---- Templates --------------------------------------------------------------
    listTemplates: {
      method: 'GET',
      path: '/templates',
      query: paginationQuerySchema,
      responses: { 200: paginatedSchema(offboardingTemplateSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'List offboarding templates',
      metadata: routeMeta('offboarding:manage'),
    },
    getTemplate: {
      method: 'GET',
      path: '/templates/:templateId',
      responses: { 200: offboardingTemplateSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Get one template with its stages and task templates',
      metadata: routeMeta('offboarding:manage'),
    },
    createTemplate: {
      method: 'POST',
      path: '/templates',
      body: createOffboardingTemplateBodySchema,
      responses: { 201: offboardingTemplateSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Create an offboarding template',
      metadata: routeMeta('offboarding:manage'),
    },
    updateTemplate: {
      method: 'PATCH',
      path: '/templates/:templateId',
      body: updateOffboardingTemplateBodySchema,
      responses: {
        200: offboardingTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update a template',
      metadata: routeMeta('offboarding:manage'),
    },
    createStage: {
      method: 'POST',
      path: '/templates/:templateId/stages',
      body: createStageBodySchema,
      responses: { 201: offboardingStageSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Add a stage to a template',
      metadata: routeMeta('offboarding:manage'),
    },
    updateStage: {
      method: 'PATCH',
      path: '/stages/:stageId',
      body: updateStageBodySchema,
      responses: { 200: offboardingStageSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Update a stage (name, description, or its automation flags)',
      metadata: routeMeta('offboarding:manage'),
    },
    reorderStages: {
      method: 'PATCH',
      path: '/templates/:templateId/stages/reorder',
      body: reorderStagesBodySchema,
      responses: {
        200: offboardingTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Reorder a template’s stages',
      metadata: routeMeta('offboarding:manage'),
    },
    deleteStage: {
      method: 'DELETE',
      path: '/stages/:stageId',
      body: c.noBody(),
      responses: {
        204: c.noBody(),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete a stage — refused (409) if any active process is currently on it',
      metadata: routeMeta('offboarding:manage'),
    },
    createTaskTemplate: {
      method: 'POST',
      path: '/stages/:stageId/task-templates',
      body: createTaskTemplateBodySchema,
      responses: {
        201: offboardingTaskTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Add a task template to a stage',
      metadata: routeMeta('offboarding:manage'),
    },
    updateTaskTemplate: {
      method: 'PATCH',
      path: '/task-templates/:taskTemplateId',
      body: updateTaskTemplateBodySchema,
      responses: {
        200: offboardingTaskTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update a task template',
      metadata: routeMeta('offboarding:manage'),
    },
    deleteTaskTemplate: {
      method: 'DELETE',
      path: '/task-templates/:taskTemplateId',
      body: c.noBody(),
      responses: { 204: c.noBody(), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Delete a task template',
      metadata: routeMeta('offboarding:manage'),
    },

    // ---- Processes ----------------------------------------------------------------
    listProcesses: {
      method: 'GET',
      path: '/processes',
      query: listProcessesQuerySchema,
      responses: { 200: paginatedSchema(offboardingProcessSchema), 401: errorBodySchema },
      summary: 'List offboarding processes in the caller’s visible scope',
      metadata: routeMeta(['offboarding:read', 'offboarding.team:read']),
    },
    getMyProcess: {
      method: 'GET',
      path: '/processes/my',
      responses: {
        200: offboardingProcessDetailSchema.nullable(),
        401: errorBodySchema,
      },
      summary: 'Get the caller’s own offboarding process, or null if they have none',
      metadata: routeMeta(null),
    },
    getProcess: {
      method: 'GET',
      path: '/processes/:processId',
      responses: {
        200: offboardingProcessDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one process with its full task list',
      // Signed-in-only: org-wide/team-scoped read, or the process's own subject — handler enforces.
      metadata: routeMeta(null),
    },
    startProcess: {
      method: 'POST',
      path: '/processes',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: startProcessBodySchema,
      responses: {
        201: offboardingProcessDetailSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Start an offboarding process directly (the involuntary-exit / manual-add path)',
      metadata: routeMeta('offboarding:manage'),
    },
    moveStage: {
      method: 'POST',
      path: '/processes/:processId/stage',
      body: moveStageBodySchema,
      responses: {
        200: offboardingProcessDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Move a process to a different stage — 412 if the current stage requires its tasks first',
      metadata: routeMeta('offboarding:manage'),
    },
    holdProcess: {
      method: 'POST',
      path: '/processes/:processId/hold',
      body: c.noBody(),
      responses: { 200: offboardingProcessDetailSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Put a process on hold',
      metadata: routeMeta('offboarding:manage'),
    },
    resumeProcess: {
      method: 'POST',
      path: '/processes/:processId/resume',
      body: c.noBody(),
      responses: { 200: offboardingProcessDetailSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Resume a held process',
      metadata: routeMeta('offboarding:manage'),
    },
    cancelProcess: {
      method: 'POST',
      path: '/processes/:processId/cancel',
      body: cancelProcessBodySchema,
      responses: { 200: offboardingProcessDetailSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Cancel a process (e.g. a resignation revoked, or an involuntary exit reversed)',
      metadata: routeMeta('offboarding:manage'),
    },
    archiveProcess: {
      method: 'POST',
      path: '/processes/:processId/archive',
      body: c.noBody(),
      responses: {
        200: offboardingProcessDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Archive a process and flip employment status to exited — 412 if a blocking gate is unmet',
      metadata: routeMeta('offboarding:manage'),
    },
    getDashboard: {
      method: 'GET',
      path: '/dashboard',
      query: getDashboardQuerySchema,
      responses: { 200: offboardingDashboardSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Offboarding dashboard KPIs',
      metadata: routeMeta('offboarding:read'),
    },
    getNoticeTracker: {
      method: 'GET',
      path: '/notice-tracker',
      responses: { 200: z.array(noticeTrackerEntrySchema), 401: errorBodySchema },
      summary: 'Who is currently in their notice period and how many days remain',
      metadata: routeMeta(['offboarding:read', 'offboarding.team:read']),
    },

    // ---- Tasks ----------------------------------------------------------------
    listMyTasks: {
      method: 'GET',
      path: '/tasks/my',
      query: listMyTasksQuerySchema,
      responses: { 200: paginatedSchema(offboardingTaskSchema), 401: errorBodySchema },
      summary: 'List offboarding tasks assigned to the caller',
      metadata: routeMeta(null),
    },
    createTask: {
      method: 'POST',
      path: '/processes/:processId/tasks',
      body: createOffboardingTaskBodySchema,
      responses: { 201: offboardingTaskSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Add a one-off task to a process (beyond its template)',
      metadata: routeMeta('offboarding:manage'),
    },
    updateTask: {
      method: 'PATCH',
      path: '/tasks/:taskId',
      body: updateOffboardingTaskBodySchema,
      responses: { 200: offboardingTaskSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Update/complete a task — assignee (self), manager, HR, IT, or finance',
      // Signed-in-only: assignee/self bypass has no permission behind it — handler enforces.
      metadata: routeMeta(null),
    },
    assignHandover: {
      method: 'POST',
      path: '/tasks/:taskId/handover',
      body: assignHandoverBodySchema,
      responses: { 200: offboardingTaskSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Assign who a knowledge-transfer/handover task hands work to',
      metadata: routeMeta(['offboarding:manage', 'offboarding.team:read']),
    },

    // ---- Exit interview ---------------------------------------------------------
    listInterviewTemplates: {
      method: 'GET',
      path: '/interview-templates',
      responses: { 200: z.array(exitInterviewTemplateSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'List exit-interview templates',
      metadata: routeMeta('offboarding:manage'),
    },
    createInterviewTemplate: {
      method: 'POST',
      path: '/interview-templates',
      body: createInterviewTemplateBodySchema,
      responses: { 201: exitInterviewTemplateSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: 'Create an exit-interview template',
      metadata: routeMeta('offboarding:manage'),
    },
    updateInterviewTemplate: {
      method: 'PATCH',
      path: '/interview-templates/:templateId',
      body: updateInterviewTemplateBodySchema,
      responses: {
        200: exitInterviewTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update an exit-interview template',
      metadata: routeMeta('offboarding:manage'),
    },
    getMyExitInterview: {
      method: 'GET',
      path: '/processes/my/exit-interview',
      responses: {
        200: exitInterviewResponseSchema.nullable(),
        401: errorBodySchema,
      },
      summary: 'Get the caller’s own exit-interview response, if any',
      metadata: routeMeta(null),
    },
    getMyExitInterviewTemplate: {
      method: 'GET',
      path: '/processes/my/exit-interview/template',
      responses: {
        200: exitInterviewTemplateSchema.nullable(),
        401: errorBodySchema,
      },
      summary: 'Get the active exit-interview template the caller answers (null if they have no active process)',
      metadata: routeMeta(null),
    },
    submitMyExitInterview: {
      method: 'POST',
      path: '/processes/my/exit-interview',
      body: submitExitInterviewBodySchema,
      responses: {
        201: exitInterviewResponseSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Submit the caller’s own exit-interview answers',
      metadata: routeMeta(null),
    },
    recordExitInterview: {
      method: 'POST',
      path: '/processes/:processId/exit-interview',
      body: recordExitInterviewBodySchema,
      responses: {
        201: exitInterviewResponseSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'HR records exit-interview answers on the employee’s behalf',
      metadata: routeMeta('exit_interview:conduct'),
    },
    getExitInterview: {
      method: 'GET',
      path: '/processes/:processId/exit-interview',
      responses: {
        200: exitInterviewResponseSchema.nullable(),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get a process’s exit-interview response',
      metadata: routeMeta('offboarding:read'),
    },

    // ---- Assets ----------------------------------------------------------------
    listEmployeeAssets: {
      method: 'GET',
      path: '/processes/:processId/assets',
      responses: { 200: z.array(employeeAssetSchema), 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'List assets issued to the employee behind this process',
      // Signed-in-only: org-wide/team-scoped read, or the process's own subject — handler enforces.
      metadata: routeMeta(null),
    },
    listUnreturnedAssets: {
      method: 'GET',
      path: '/assets/unreturned',
      query: listUnreturnedAssetsQuerySchema,
      responses: { 200: paginatedSchema(employeeAssetSchema), 401: errorBodySchema, 403: errorBodySchema },
      summary: 'List unreturned assets across active offboarding processes',
      metadata: routeMeta(['offboarding:read', 'asset:manage']),
    },
    addEmployeeAsset: {
      method: 'POST',
      path: '/processes/:processId/assets',
      body: addEmployeeAssetBodySchema,
      responses: { 201: employeeAssetSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Record an asset issued to the employee, for return tracking',
      metadata: routeMeta(['asset:manage', 'offboarding:manage']),
    },
    markAssetReturned: {
      method: 'POST',
      path: '/assets/:assetId/return',
      body: markAssetReturnedBodySchema,
      responses: { 200: employeeAssetSchema, 401: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      summary: 'Mark an asset as returned',
      metadata: routeMeta('asset:manage'),
    },
  },
  { pathPrefix: '/offboarding' },
);
export type OffboardingContract = typeof offboardingContract;
